import * as React from 'react';
import * as autoBind from 'react-autobind';
import * as classNames from 'classnames';
import {TscAvatarSize, ControlButton, TscButtonType} from '@techsmith/tsc-cloud-style-guide';
import EditBox from '../EditBox/EditBox';
import ReviewService from '../../services/ReviewService/ReviewService';
import TimeFormatter from '../../utils/TimeFormatter/TimeFormatter';
import UserMessaging from '../../utils/UserMessaging';
import Strings from '../../globals/Strings';
import AvatarWrapper from '../AvatarWrapper/AvatarWrapper';
import LocalStorage from '../../services/LocalStorageWrapper/LocalStorageWrapper';
import CommentModel from '../../types/CommentModel';
import Constants, {UserTypes, ButtonActivationKeys} from '../../globals/Constants';
import ReviewRouterService from '../../services/ReviewRouterService/ReviewRouterService';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import LikeButtonContainer from '../LikeButton/LikeButtonContainer';
import GoogleAnalyticsService from '../../services/GoogleAnalyticsService/GoogleAnalyticsService';
import DeleteCommentModal from '../DeleteCommentModal/DeleteCommentModal';
import Linkify from 'react-linkify';
import {BasicCommentProps} from './BasicCommentContainer';

export default class BasicComment extends React.Component<BasicCommentProps, BasicCommentState> {
   static defaultProps: Partial<BasicCommentProps> = {
      onSaveEdit: () => null,
      onCancelEdit: () => null
   };

   readonly state: BasicCommentState = {saving: false, likeInProgress: false, showAlert: false};
   unsavedEdit: string;
   comment: HTMLElement;
   componentIsMounted: boolean;

   constructor(props: BasicCommentProps) {
      super(props);
      autoBind(this);
      this.unsavedEdit = LocalStorage.getItem(LocalStorage.getKey(Constants.localStorageKeys.editComment, props.comment.id));
   }

   componentDidMount() {
      this.componentIsMounted = true;
   }

   componentWillUnmount() {
      this.componentIsMounted = false;
   }

   componentDidUpdate(prevProps: BasicCommentProps) {
      const selfIdentifiedLoggedOut = prevProps.currentUser.userType === UserTypes.selfIdentified && (!this.props.currentUser || !this.props.currentUser.userId);
      if (selfIdentifiedLoggedOut) {
         LocalStorage.removeItem(LocalStorage.getKey(Constants.localStorageKeys.editComment, prevProps.comment.id));
         this.unsavedEdit = null;
         this.cancelEdit();
      }
   }

   onStartEdit(e: any): void {
      const {onEditClick, onEditing, comment} = this.props;

      onEditing(comment.id);
      onEditClick();

      e && e.stopPropagation && e.stopPropagation();
   }

   cancelEdit(): void {
      const {onEditing, onCancelEdit} = this.props;

      onEditing();
      onCancelEdit();
   }

   saveEdit(text: string): Promise<void> {
      const {deleteAnnotationOnSave, comment, onEditing, onSaveEdit, currentUser, isOwner} = this.props;
      let commentCopy;

      if ((!text || text === Strings.defaults.annotationAttached) && (!comment.annotation || deleteAnnotationOnSave)) {
         this.deleteComment();
         return;
      }

      if (deleteAnnotationOnSave) {
         commentCopy = Object.assign({}, comment, {text, annotation: ''});
      } else {
         commentCopy = Object.assign({}, comment, {text});
      }

      if (!this.state.saving) {
         this.setState({saving: true});
         return ReviewService.editComment(commentCopy).then(() => {
            this.setState({saving: false});
            const gaLabel = isOwner ? Constants.googleAnalytics.commenting.labels.reviewOwner : Constants.googleAnalytics.commenting.labels.nonReviewOwner;
            const actionLabel = text ? Constants.googleAnalytics.commenting.actions.editComment : Constants.googleAnalytics.commenting.actions.deleteCommentText;
            GoogleAnalyticsService.trackCommenting(currentUser.userId, actionLabel, gaLabel);
            onEditing();
            onSaveEdit();
         }).catch(() => {
            UserMessaging.warn(Strings.errors.failedSaveComment);
            this.setState({saving: false});
         });
      }
   }

   onPerformDeletion(): Promise<void> {
      if (!this.state.saving) {
         const {comment, onEditing, onSaveEdit, setSelectedComment, currentUser, isOwner} = this.props;

         this.setState({saving: true});
         return ReviewService.deleteComment(comment).then(() => {
            const gaLabel = isOwner ? Constants.googleAnalytics.commenting.labels.reviewOwner : Constants.googleAnalytics.commenting.labels.nonReviewOwner;
            GoogleAnalyticsService.trackCommenting(currentUser.userId, Constants.googleAnalytics.commenting.actions.deleteComment, gaLabel);
            setSelectedComment(null);
            this.setState({showAlert: false});

            if (this.componentIsMounted) {
               this.setState({saving: false});
               onEditing();
               onSaveEdit();
            }
         }).catch(() => {
            UserMessaging.warn(Strings.errors.failedDeleteComment);
            this.setState({saving: false});
         });
      }
   }

   onCancelDeletion(): void {
      this.setState({showAlert: false});
   }

   deleteComment(): void {
      this.setState({showAlert: true});
   }

   setUnsavedEdit(text: string): void {
      this.unsavedEdit = text;
   }

   onReplyClick(e: any): void {
      e.stopPropagation();
      this.props.onReplyClick();
   }

   onKeyPress(e: any): void {
      if (e.keyCode in ButtonActivationKeys && !e.shiftKey) {
         // e.preventDefault();
         this.onClick();
      }
   }

   onClick() {
      const {history, comment, reviewId, player, onSelect, reviewVersion} = this.props;
      onSelect();
      ReviewRouterService.navigateToReviewComment(history, reviewId, reviewVersion.VersionNumber, comment.id);
      ReviewRouterService.navigateToReviewOnTimeMismatch(history, player, reviewId, reviewVersion.VersionNumber, comment.id, comment.time);
   }

   getCommentText(comment: CommentModel): string {
      if (comment.isDeleted || (!comment.text && !comment.annotation)) {
         return Strings.defaults.emptyComment;
      }
      if (!comment.text) {
         return Strings.defaults.annotationAttached;
      }
      return comment.text;
   }

   render() {
      const {comment, currentUser, isEditable, isEditing, deleteAnnotationOnSave, themeName, alternateDisplayText, children, isOwner, review, reviewVersion} = this.props;
      const {saving, showAlert} = this.state;

      const user = comment.user;
      const isAuthor = user && user.userId === currentUser.userId;
      const textClasses = classNames('comment-text', {italics: !comment.text || comment.text === Strings.defaults.annotationAttached});
      const time = alternateDisplayText ? alternateDisplayText : comment.lastModifiedDate ? `Edited - ${TimeFormatter.formatDate(comment.lastModifiedDate)}` : TimeFormatter.formatDate(comment.createDate);
      const isParent = comment.parentCommentId === null;
      const isLatestVersion = review.ReviewVersions.filter(ver => !ver.IsDeleted).pop().VersionNumber === reviewVersion.VersionNumber;
      const showEditButton = !isEditing && (isAuthor || isOwner) && isEditable && (isLatestVersion || isOwner);
      const showLikeButton = !isEditing && !comment.isDeleted && isLatestVersion;
      const showReplyButton = isParent && !isEditing && (isLatestVersion || comment.replies.length >= 1);
      const isEditBoxReadOnly = !(isAuthor && isEditable);
      const showEditedLabel = !comment.isDeleted && comment.lastModifiedDate !== null;
      const showUnsavedEdit = !isEditing && (deleteAnnotationOnSave || (this.unsavedEdit != null && this.unsavedEdit !== comment.text)) && !comment.isResolved;
      const alertContentClasses = saving ? 'alert-body' : 'alert';
      const commentAriaLabel = !comment.parentCommentId ?
               Strings.accessibility.comments.parentCommentAriaLabel.replace('{user}', comment.user.name).replace('{time}', TimeFormatter.formatTimeForSpeech(comment.time)).replace('{commentText}', comment.text).replace('{annotationStatus}', comment.annotation ? Strings.accessibility.comments.annotationAttached : '')
               : Strings.accessibility.comments.replyAriaLabel.replace('{user}', comment.user.name).replace('{commentText}', comment.text);

      const alertContent =
         <div className={alertContentClasses}>
            {saving && <LoadingSpinner/>}
            {!saving && Strings.labels.confirmCommentDeletionText}
         </div>;

      const commentTextContent =
         <Linkify>
            <span className={textClasses} tabIndex={0} aria-label={commentAriaLabel} role="text">
               {this.getCommentText(comment)}
               {showEditedLabel && <span className="edit-label">{Strings.labels.edited}</span>}
            </span>
         </Linkify>;

      return (
         <div className="basic-comment-wrapper" onClick={this.onClick}>
            <div className="basic-comment" ref={(c: any) => {
               this.comment = c;
            }}>
               <div className="comment-header">
                  {user && <AvatarWrapper user={user} size={TscAvatarSize.small}/>}
                  {user && <div title={user.name} className="user-name">{user.name}</div>}
                  {children}
               </div>
               <div title={time} className="comment-body">
                  {!isEditing && commentTextContent}
                  {isEditing && <EditBox themeName={themeName}
                                       originalText={comment.text}
                                       cancel={this.cancelEdit}
                                       save={this.saveEdit}
                                       saving={saving}
                                       deleteText={this.deleteComment}
                                       commentId={comment.id}
                                       setUnsavedEdit={this.setUnsavedEdit}
                                       deleteAnnotationOnSave={deleteAnnotationOnSave}
                                       readOnly={isEditBoxReadOnly}
                                       showEditedLabel={showEditedLabel}
                                       commentHasAnnotation={comment.annotation !== ''}/>}
                  {showUnsavedEdit && <div className="unsaved-edit">{Strings.labels.unsavedEdit}</div>}
               </div>
               <div className="comment-footer">
                  {showReplyButton && <ControlButton onClick={this.onReplyClick}
                                                     label={Strings.labels.reply}
                                                     themeName={themeName}
                                                     buttonType={TscButtonType.floating}
                                                     testId="comment-reply-btn" />}
                  {showLikeButton && <LikeButtonContainer comment={comment}/>}
                  {showEditButton && <ControlButton onClick={this.onStartEdit}
                                                    label={Strings.labels.edit}
                                                    themeName={themeName}
                                                    buttonType={TscButtonType.floating}
                                                    testId="comment-edit-btn" />}
               </div>
               {showAlert && <DeleteCommentModal alertContent={alertContent}
                                                saving={saving}
                                                onCancelDeletion={this.onCancelDeletion}
                                                onPerformDeletion={this.onPerformDeletion}/>}
            </div>
         </div>
      );
   }
}

interface BasicCommentState {
   saving: boolean;
   likeInProgress: boolean;
   showAlert: boolean;
}
